import React, { useEffect, useRef, useState, useCallback } from 'react';
import { drawConstellation, getStarRadius } from './constellationDrawing';
import { sanitizeUniverseTheme } from './themeSafety';
import { track } from '../../lib/analytics';
import { UNIVERSE_PAUSE_EVENT, UNIVERSE_RESUME_EVENT } from '../ModalShell';
import { 
  GESTURE_MODES, 
  createGestureState, 
  clampOffset, 
  clampScale, 
  isPinchValid,
  SCALE_MIN,
  SCALE_MAX
} from './gestureState';
import { UniverseDebugPanel } from './UniverseDebugPanel';
import { Z_LAYERS } from './zLayers';

// Production-safe debug logging
const DEBUG_UNIVERSE = typeof window !== 'undefined' && 
  process.env.NODE_ENV !== 'production' && 
  localStorage.getItem('ul_debug_universe') === '1';

const debugLog = (...args) => {
  if (DEBUG_UNIVERSE) console.log(...args);
};

// Maximum offset radius (prevents infinite panning)
const MAX_OFFSET_RADIUS = 1500;

/**
 * Constellation Canvas Component - M5: Canvas Renderer + M6: Interaction Layer
 * 
 * Renders an interactive star map visualization of user connections
 * 
 * Features:
 * - Core star (current user) at center with aura color
 * - Connection nodes positioned at (x, y) coordinates
 * - Lines connecting core to each node
 * - Visual distinction by connection type (light/strong/bonded)
 * - Pan: Click and drag to move the view
 * - Zoom: Scroll wheel to zoom in/out
 * - Click/Tap: Select a star to view profile (M6)
 * 
 * @param {Object} props
 * @param {Object} props.coreStar - Current user's star data
 * @param {Array} props.nodes - Connected user nodes
 * @param {Array} props.edges - Connection edges
 * @param {Function} props.onNodeSelected - Callback when a node is selected
 * @param {boolean} props.disableClear - If true, won't emit null selections (when card is open)
 */
export const ConstellationCanvas = ({ coreStar, nodes, edges, onNodeSelected, disableClear, theme, onHitDebug }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  
  // Canvas size (needs state for resize observer)
  const [size, setSize] = useState({ width: 0, height: 0 });
  
  // PERFORMANCE FIX: Move pan/zoom to refs (no re-renders on hot path)
  const viewRef = useRef({ x: 0, y: 0, scale: 1 });
  const needsDrawRef = useRef(true);
  
  // PERF: Universe stays mounted under modal overlays (profile/chat).
  // We pause all rendering/animation while an overlay is open to avoid main-thread stalls.
  const pausedRef = useRef(false);
  const runningRef = useRef(false);
  const animateFnRef = useRef(null);
  
  // State mirrors for UI display only (updated sparingly)
  const [displayScale, setDisplayScale] = useState(1);
  
  // Visibility state for pausing animation when tab hidden
  const [isVisible, setIsVisible] = useState(true);

  // Request a redraw and (re)start RAF loop if it was stopped
  const requestDraw = useCallback(() => {
    needsDrawRef.current = true;
    if (pausedRef.current) return;
    if (!isVisible) return;
    if (runningRef.current) return;
    if (!animateFnRef.current) return;
    runningRef.current = true;
    animationRef.current = requestAnimationFrame(animateFnRef.current);
  }, [isVisible]);
  
  // Debug panel state (tracks current gesture mode for display)
  const [debugGestureMode, setDebugGestureMode] = useState(GESTURE_MODES.IDLE);
  
  // Tap-after-zoom debug state (shows hit/miss info in debug panel)
  const [localHitDebug, setLocalHitDebug] = useState(null);
  
  // Gesture state machine (single source of truth)
  const gestureRef = useRef(createGestureState());
  
  // Subscribe to gesture mode changes for debug panel
  useEffect(() => {
    const unsubscribe = gestureRef.current.subscribe((mode) => {
      setDebugGestureMode(mode);
    });
    return unsubscribe;
  }, []);
  
  // Velocity state for inertial panning
  const velocityRef = useRef({ vx: 0, vy: 0 });
  const lastMoveTimeRef = useRef(0);
  const inertiaAnimationRef = useRef(null);
  
  // Pinch zoom state
  const pinchStartRef = useRef(null); // { distance, scale, center }
  const activeTouchesRef = useRef(new Map()); // Track active touch points
  
  // Ripple effects (for tap feedback)
  const ripplesRef = useRef([]); // {x, y, t0, color}
  
  // Pan/zoom tracking (fire once per session)
  const didPanZoomRef = useRef(false);
  
  // Animation state
  const animationRef = useRef(null);
  const lastFrameTimeRef = useRef(0);
  
  // Effective theme with defaults + safety clamping
  const unsafeTheme = theme || {
    id: 'classic',
    bg: { nebula: false, starfieldDensity: 80, grain: 0.05 },
    fx: { glow: 0.35, shimmer: 0.0, ripples: false },
    motion: { twinkle: 0.15, drift: 0.05, pulse: 0.15 },
    lines: { opacity: 0.55, width: 1.0, energy: 0.0 },
    nodes: { baseRadius: 1.0, aura: 0.5 }
  };
  const effectiveTheme = sanitizeUniverseTheme(unsafeTheme);
  
  // Cache screen coordinates for hit testing (must match drawing coords exactly)
  const screenNodesRef = useRef([]);
  
  // Pan/drag state - battle-tested pattern
  const isDragging = useRef(false);
  const lastPos = useRef(null);
  const pointerDownPos = useRef(null);
  const pointerDownTime = useRef(null);
  const hasStartedDragging = useRef(false);

  // Visibility change handler - pause animation when tab hidden (saves battery)
  useEffect(() => {
    const handleVisibility = () => {
      const visible = document.visibilityState === 'visible';
      setIsVisible(visible);
      debugLog('[Visibility]', visible ? 'resumed' : 'paused');
      
      if (!visible && inertiaAnimationRef.current) {
        // Kill inertia when tab hidden
        cancelAnimationFrame(inertiaAnimationRef.current);
        inertiaAnimationRef.current = null;
        velocityRef.current = { vx: 0, vy: 0 };
        gestureRef.current.end();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Overlay pause/resume (Universe stays mounted under modal routes)
  useEffect(() => {
    const pause = () => {
      pausedRef.current = true;
      runningRef.current = false;
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      if (inertiaAnimationRef.current) {
        cancelAnimationFrame(inertiaAnimationRef.current);
        inertiaAnimationRef.current = null;
      }
    };

    const resume = () => {
      pausedRef.current = false;
      requestDraw();
      if (!isVisible) return;
      if (!animateFnRef.current) return;
      if (runningRef.current) return;
      runningRef.current = true;
      animationRef.current = requestAnimationFrame(animateFnRef.current);
    };

    window.addEventListener(UNIVERSE_PAUSE_EVENT, pause);
    window.addEventListener(UNIVERSE_RESUME_EVENT, resume);
    return () => {
      window.removeEventListener(UNIVERSE_PAUSE_EVENT, pause);
      window.removeEventListener(UNIVERSE_RESUME_EVENT, resume);
    };
  }, [isVisible, requestDraw]);

  // Ripple drawing function (defined before useEffect that uses it)
  // Respects Power Saver (disabled) and Reduce Motion (shorter/smaller)
  const drawRipples = (ctx, now, theme, ripplesRefArg) => {
    const powerSaver = !!theme?.powerSaver;
    
    // In Power Saver mode, don't draw ripples and clear any existing
    if (powerSaver) {
      ripplesRefArg.current = [];
      return;
    }

    const next = [];

    for (const r of ripplesRefArg.current) {
      // Use per-ripple duration/maxRadius if provided, otherwise defaults
      const life = r.duration || 450;
      const maxR = r.maxRadius || 28;
      
      const age = now - r.t0;
      if (age >= life) continue;

      // FIX: Clamp progress to prevent negative radius crash
      const p = Math.min(1, Math.max(0, age / life)); // 0..1 clamped
      const radius = Math.max(0, 6 + p * maxR); // Never negative
      const alpha = Math.max(0, (1 - p) * 0.4);
      
      // Skip if radius would be invalid
      if (radius <= 0 || !isFinite(radius)) continue;

      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = r.color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(r.x, r.y, radius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();

      next.push(r);
    }

    ripplesRefArg.current = next;
  };

  // Resize observer - update canvas size when container resizes
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const resize = () => {
      setSize({ width: el.clientWidth, height: el.clientHeight });
    };
    resize();

    const observer = new ResizeObserver(() => resize());
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Animation loop for visual effects - PERFORMANCE OPTIMIZED
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || size.width === 0 || size.height === 0) return;

    // Skip animation when tab is hidden (saves battery)
    if (!isVisible) {
      debugLog('[Render] Animation paused (tab hidden)');
      return;
    }

    // If a modal overlay is open, pause all rendering (Universe remains mounted underneath)
    if (pausedRef.current) {
      debugLog('[Render] Animation paused (overlay open)');
      return;
    }

    // Set canvas resolution (accounting for device pixel ratio)
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size.width * dpr;
    canvas.height = size.height * dpr;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Check if animations are enabled
    const hasAnimation =
      effectiveTheme.motion?.twinkle > 0 ||
      effectiveTheme.motion?.drift > 0 ||
      effectiveTheme.motion?.pulse > 0 ||
      effectiveTheme.lines?.energy > 0 ||
      effectiveTheme.fx?.shimmer > 0 ||
      effectiveTheme.bg?.nebula;

    const stopAllAnimation = () => {
      runningRef.current = false;
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      if (inertiaAnimationRef.current) {
        cancelAnimationFrame(inertiaAnimationRef.current);
        inertiaAnimationRef.current = null;
      }
    };

    const ensureRunning = () => {
      if (runningRef.current) return;
      if (pausedRef.current) return;
      if (!isVisible) return;
      // If nothing is animating and we don't need a draw, don't start a loop.
      if (!hasAnimation && !needsDrawRef.current) return;
      if (!animateFnRef.current) return;
      runningRef.current = true;
      animationRef.current = requestAnimationFrame(animateFnRef.current);
    };

    const animate = (timestamp) => {
      // Bail fast if paused while a modal is open
      if (pausedRef.current || !isVisible) {
        stopAllAnimation();
        return;
      }

      // If theme has no animation and we have no interaction-driven draw requests,
      // stop the RAF loop entirely (battery + main-thread protection).
      if (!hasAnimation && !needsDrawRef.current) {
        stopAllAnimation();
        return;
      }

      // Throttle to ~30fps max to save battery (every 33ms)
      // But ALWAYS draw if needsDrawRef is true (user interaction)
      const timeSinceLastFrame = timestamp - lastFrameTimeRef.current;
      if (!needsDrawRef.current && timeSinceLastFrame < 33) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }
      lastFrameTimeRef.current = timestamp;
      needsDrawRef.current = false;

      const { x: offsetX, y: offsetY, scale: currentScale } = viewRef.current;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const { width, height } = size;
      const cx = width / 2 + offsetX;
      const cy = height / 2 + offsetY;

      const screenNodes = nodes.map((node) => {
        const px = cx + node.x * (width / 2) * 0.7 * currentScale;
        const py = cy + node.y * (height / 2) * 0.7 * currentScale;

        const baseRadius =
          node.connection_type === 'bonded' ? 8 : node.connection_type === 'strong' ? 6 : 4;
        const hitRadius = baseRadius + 30;

        return {
          id: node.user_id,
          screenX: px,
          screenY: py,
          hitRadius,
          raw: node
        };
      });

      screenNodesRef.current = screenNodes;

      drawConstellation(
        ctx,
        size.width,
        size.height,
        { coreStar, nodes, edges },
        currentScale,
        { x: offsetX, y: offsetY },
        effectiveTheme,
        timestamp
      );

      try {
        drawRipples(ctx, timestamp, effectiveTheme, ripplesRef);
      } catch (e) {
        console.warn('[Ripples] draw failed (non-fatal):', e);
        ripplesRef.current = [];
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animateFnRef.current = animate;
    ensureRunning();

    return () => {
      stopAllAnimation();
      animateFnRef.current = null;
    };
  }, [coreStar, nodes, edges, size, effectiveTheme, isVisible]);

  // Hit detection now uses cached screen coordinates from render
  // (removed old detectStarHit function - coordinates are cached in useEffect)

  // Helper: track pan/zoom once per session
  const markPanZoom = (type) => {
    if (didPanZoomRef.current) return;
    didPanZoomRef.current = true;
    track("universe_pan_zoom_used", { type });
  };

  // Inertia animation (momentum after pan release) - USES REFS
  const startInertia = () => {
    // Skip inertia if Reduce Motion is enabled
    if (effectiveTheme?.reduceMotion) {
      velocityRef.current = { vx: 0, vy: 0 };
      gestureRef.current.end();
      return;
    }
    
    const { vx, vy } = velocityRef.current;
    const minVelocity = 0.5;
    const friction = 0.92; // Decay factor per frame
    
    // Only start if we have meaningful velocity
    if (Math.abs(vx) < minVelocity && Math.abs(vy) < minVelocity) {
      velocityRef.current = { vx: 0, vy: 0 };
      gestureRef.current.end();
      return;
    }
    
    // Transition gesture state to inertia
    gestureRef.current.startInertia();
    
    // Debug breadcrumb (production-safe)
    debugLog('[Gesture] inertia start', { vx: vx.toFixed(2), vy: vy.toFixed(2) });
    
    // Cancel any existing inertia animation
    if (inertiaAnimationRef.current) {
      cancelAnimationFrame(inertiaAnimationRef.current);
    }
    
    const animateInertia = () => {
      const { vx, vy } = velocityRef.current;
      
      // Stop when velocity is negligible
      if (Math.abs(vx) < minVelocity && Math.abs(vy) < minVelocity) {
        velocityRef.current = { vx: 0, vy: 0 };
        inertiaAnimationRef.current = null;
        gestureRef.current.end();
        return;
      }
      
      // Apply velocity to view ref with clamping (NO setState!)
      const newOffset = clampOffset({
        x: viewRef.current.x + vx,
        y: viewRef.current.y + vy
      }, MAX_OFFSET_RADIUS);
      viewRef.current.x = newOffset.x;
      viewRef.current.y = newOffset.y;
      requestDraw();
      
      // Apply friction
      velocityRef.current = {
        vx: vx * friction,
        vy: vy * friction
      };
      
      inertiaAnimationRef.current = requestAnimationFrame(animateInertia);
    };
    
    inertiaAnimationRef.current = requestAnimationFrame(animateInertia);
  };

  // Calculate distance between two touch points
  const getTouchDistance = (touches) => {
    if (touches.length < 2) return 0;
    const [t1, t2] = touches;
    const dx = t2.clientX - t1.clientX;
    const dy = t2.clientY - t1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Calculate center point between two touches
  const getTouchCenter = (touches) => {
    if (touches.length < 2) return null;
    const [t1, t2] = touches;
    return {
      x: (t1.clientX + t2.clientX) / 2,
      y: (t1.clientY + t2.clientY) / 2
    };
  };

  // Pan handlers with tap detection, velocity tracking, and pinch zoom
  const handlePointerDown = (e) => {
    // Prevent default to stop browser gestures
    e.preventDefault();
    
    // Cancel any running inertia immediately
    if (inertiaAnimationRef.current) {
      cancelAnimationFrame(inertiaAnimationRef.current);
      inertiaAnimationRef.current = null;
    }
    velocityRef.current = { vx: 0, vy: 0 };
    gestureRef.current.end(); // Reset gesture state
    
    const { clientX, clientY, pointerId, pointerType } = e;
    
    // Debug breadcrumb (production-safe)
    debugLog('[Pointer] down', { pointerId, pointerType, x: clientX.toFixed(0), y: clientY.toFixed(0) });
    
    // Track this touch point
    activeTouchesRef.current.set(pointerId, { x: clientX, y: clientY });
    
    // Start panning immediately (Safari scroll prevention)
    gestureRef.current.startPan();
    isDragging.current = true;
    
    // If this is the first pointer, set up for pan/tap
    if (activeTouchesRef.current.size === 1) {
      pointerDownPos.current = { x: clientX, y: clientY };
      pointerDownTime.current = performance.now();
      lastMoveTimeRef.current = performance.now();
      hasStartedDragging.current = false;
      lastPos.current = { x: clientX, y: clientY };
    }
    
    // If this is the second pointer, set up for pinch zoom
    if (activeTouchesRef.current.size === 2) {
      const touches = Array.from(activeTouchesRef.current.values());
      const distance = getTouchDistance(touches);
      const center = getTouchCenter(touches);
      
      // Validate pinch distance
      if (!isPinchValid(distance)) {
        debugLog('[Gesture] pinch invalid distance, ignoring');
        return;
      }
      
      // Debug breadcrumb (production-safe)
      debugLog('[Gesture] pinch start', { distance: distance.toFixed(0), scale: viewRef.current.scale.toFixed(2) });
      
      // Transition to pinching mode
      gestureRef.current.startPinch();
      
      pinchStartRef.current = {
        distance,
        scale: viewRef.current.scale,
        center
      };
      
      // Kill inertia explicitly when pinch starts
      velocityRef.current = { vx: 0, vy: 0 };
      
      // Cancel single-finger drag when pinching
      hasStartedDragging.current = false;
    }
    
    // Capture pointer - critical for mobile
    try {
      e.currentTarget.setPointerCapture(pointerId);
    } catch (err) {
      // Pointer may already be captured or invalid
    }
    e.currentTarget.style.cursor = 'grabbing';
  };

  const handlePointerMove = (e) => {
    // Prevent default to stop browser scroll/zoom
    e.preventDefault();
    
    const { clientX, clientY, pointerId } = e;
    
    // Update tracked touch position
    if (activeTouchesRef.current.has(pointerId)) {
      activeTouchesRef.current.set(pointerId, { x: clientX, y: clientY });
    }
    
    // Handle pinch zoom (2 fingers)
    if (gestureRef.current.is(GESTURE_MODES.PINCHING) && pinchStartRef.current) {
      const touches = Array.from(activeTouchesRef.current.values());
      const currentDistance = getTouchDistance(touches);
      const { distance: startDistance, scale: startScale } = pinchStartRef.current;
      
      // Validate pinch math
      if (isPinchValid(startDistance) && isPinchValid(currentDistance)) {
        const zoomRatio = currentDistance / startDistance;
        const newScale = clampScale(startScale * zoomRatio);
        // Update ref instead of state (NO re-render!)
        viewRef.current.scale = newScale;
        requestDraw();
        markPanZoom("zoom");
      }
      return;
    }
    
    // Handle single-finger pan
    if (!isDragging.current || !lastPos.current || !pointerDownPos.current) return;
    
    const now = performance.now();
    const dt = now - lastMoveTimeRef.current;
    lastMoveTimeRef.current = now;
    
    const dx = clientX - lastPos.current.x;
    const dy = clientY - lastPos.current.y;
    
    // Track velocity for inertia (smoothed)
    if (dt > 0 && dt < 100) { // Ignore large time gaps
      const newVx = dx / dt * 16; // Normalize to ~60fps
      const newVy = dy / dt * 16;
      // Smooth velocity with previous
      velocityRef.current = {
        vx: velocityRef.current.vx * 0.3 + newVx * 0.7,
        vy: velocityRef.current.vy * 0.3 + newVy * 0.7
      };
    }
    
    // If movement is noticeable (> 2px), we're dragging
    const movedDistSquared = dx * dx + dy * dy;
    if (movedDistSquared > 4) {
      hasStartedDragging.current = true;
    }
    
    // Only pan if we've actually started dragging
    if (hasStartedDragging.current) {
      // Update ref instead of state (NO re-render!)
      const newOffset = clampOffset({ 
        x: viewRef.current.x + dx, 
        y: viewRef.current.y + dy 
      }, MAX_OFFSET_RADIUS);
      viewRef.current.x = newOffset.x;
      viewRef.current.y = newOffset.y;
      requestDraw();
      markPanZoom("pan");
    }
    
    lastPos.current = { x: clientX, y: clientY };
  };

  const handlePointerUp = (e) => {
    const { pointerId } = e;
    
    // Remove this touch point
    activeTouchesRef.current.delete(pointerId);
    
    // Release pointer capture
    try {
      e.currentTarget.releasePointerCapture(pointerId);
    } catch (err) {
      // Pointer may have been released already
    }
    e.currentTarget.style.cursor = 'grab';
    
    // If we still have touches, don't process as tap/end
    if (activeTouchesRef.current.size > 0) {
      // Reset pinch state if going from 2 to 1 finger
      pinchStartRef.current = null;
      gestureRef.current.startPan(); // Back to panning with remaining finger
      return;
    }
    
    // Clear pinch state
    pinchStartRef.current = null;
    
    const start = pointerDownPos.current;
    const startTime = pointerDownTime.current;
    const wasDragging = hasStartedDragging.current;
    
    // Reset state
    isDragging.current = false;
    hasStartedDragging.current = false;
    lastPos.current = null;

    if (!start || startTime == null) {
      pointerDownPos.current = null;
      pointerDownTime.current = null;
      return;
    }

    // Calculate distance and duration
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    const distSquared = dx * dx + dy * dy;
    const duration = performance.now() - startTime;

    // Mobile-friendly thresholds
    const CLICK_DISTANCE = 40; // px - more forgiving for mouse & touch
    const TAP_MAX_DURATION = 450; // ms - allow slightly slower taps

    // Treat as TAP if: didn't drag, small movement, quick action
    const isTap = 
      !wasDragging &&
      distSquared <= CLICK_DISTANCE * CLICK_DISTANCE &&
      duration <= TAP_MAX_DURATION;

    // DEBUG: Log tap detection
    console.log('[PointerUp]', {
      duration,
      distSquared,
      wasDragging,
      isTap,
      CLICK_DISTANCE,
      TAP_MAX_DURATION
    });

    // Reset pointer state
    pointerDownPos.current = null;
    pointerDownTime.current = null;

    if (isTap) {
      debugLog('[PointerUp] TAP DETECTED, calling handleCanvasTap');
      gestureRef.current.end();
      // This is a tap - run hit detection
      handleCanvasTap(e);
    } else if (wasDragging) {
      // It was a drag - start inertia if we have velocity
      debugLog('[PointerUp] DRAG END, starting inertia', velocityRef.current);
      startInertia();
    } else {
      gestureRef.current.end();
    }
  };

  // === WORLD-SPACE HIT TESTING (bulletproof against DPR/scaling issues) ===
  const HIT_RADIUS_PX = 36; // finger-sized hit area in CSS pixels
  
  // Convert CSS canvas point to world coordinates
  const screenToWorld = (cssX, cssY, canvasWidth, canvasHeight, view) => {
    // Reverse the drawing transform:
    // Drawing: px = cx + node.x * (width/2) * 0.7 * scale
    // Where: cx = width/2 + offset.x
    // So: px = width/2 + offset.x + node.x * (width/2) * 0.7 * scale
    // Solving for node.x: node.x = (px - width/2 - offset.x) / ((width/2) * 0.7 * scale)
    const cx = canvasWidth / 2 + view.x;
    const cy = canvasHeight / 2 + view.y;
    const scaleFactorX = (canvasWidth / 2) * 0.7 * view.scale;
    const scaleFactorY = (canvasHeight / 2) * 0.7 * view.scale;
    
    return {
      x: (cssX - cx) / scaleFactorX,
      y: (cssY - cy) / scaleFactorY
    };
  };

  // Simple onClick handler - uses WORLD SPACE hit testing
  const handleCanvasClick = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Step 1: Get click in CSS canvas coords
    const rect = canvas.getBoundingClientRect();
    const cssX = e.clientX - rect.left;
    const cssY = e.clientY - rect.top;
    const view = viewRef.current;

    console.log('[CanvasClick] CSS coords:', { 
      cssX: cssX.toFixed(1), 
      cssY: cssY.toFixed(1),
      canvasWidth: rect.width.toFixed(0),
      canvasHeight: rect.height.toFixed(0),
      scale: view.scale.toFixed(3),
      offsetX: view.x.toFixed(1),
      offsetY: view.y.toFixed(1)
    });

    // Step 2: Convert to world space
    const world = screenToWorld(cssX, cssY, rect.width, rect.height, view);
    console.log('[CanvasClick] World coords:', { 
      worldX: world.x.toFixed(4), 
      worldY: world.y.toFixed(4) 
    });

    // Step 3: Calculate hit radius in world space (adapts to zoom)
    // At scale=1, HIT_RADIUS_PX pixels = some world units
    // As you zoom in (scale increases), the world-space hit area shrinks
    const scaleFactorX = (rect.width / 2) * 0.7 * view.scale;
    const hitWorldRadius = HIT_RADIUS_PX / scaleFactorX;
    
    console.log('[CanvasClick] Hit radius (world):', hitWorldRadius.toFixed(4));

    // Step 4: Test against all nodes in WORLD SPACE
    if (!nodes || nodes.length === 0) {
      console.log('[CanvasClick] No nodes to test');
      return;
    }

    let hitNode = null;
    let bestDist = Infinity;

    for (const node of nodes) {
      // node.x and node.y are already in world space (-1 to 1 range)
      const dx = node.x - world.x;
      const dy = node.y - world.y;
      const dist = Math.hypot(dx, dy);

      if (dist <= hitWorldRadius && dist < bestDist) {
        bestDist = dist;
        hitNode = node;
      }
    }

    // Debug: log closest nodes
    const sortedByDist = [...nodes]
      .map(n => ({ 
        name: n.display_name || n.user_id?.slice(0, 8), 
        dist: Math.hypot(n.x - world.x, n.y - world.y).toFixed(4),
        worldX: n.x.toFixed(4),
        worldY: n.y.toFixed(4)
      }))
      .sort((a, b) => parseFloat(a.dist) - parseFloat(b.dist))
      .slice(0, 3);
    console.log('[CanvasClick] Closest 3 nodes:', sortedByDist);

    if (hitNode) {
      console.log('[CanvasClick] HIT →', hitNode.display_name || hitNode.user_id, 
        `(dist: ${bestDist.toFixed(4)}, threshold: ${hitWorldRadius.toFixed(4)})`);
      
      // Calculate screen position for ripple effect
      const cx = rect.width / 2 + view.x;
      const cy = rect.height / 2 + view.y;
      const hitScreenX = cx + hitNode.x * (rect.width / 2) * 0.7 * view.scale;
      const hitScreenY = cy + hitNode.y * (rect.height / 2) * 0.7 * view.scale;
      
      // Check Power Saver and Reduce Motion from theme
      const powerSaver = !!effectiveTheme?.powerSaver;
      const reduceMotion = !!effectiveTheme?.reduceMotion;
      
      // 🎯 HAPTIC FEEDBACK: Best-effort vibration on successful hit
      const shouldHaptic = !reduceMotion && !powerSaver;
      if (shouldHaptic && navigator?.vibrate) {
        try {
          navigator.vibrate(12); // tiny "tick"
        } catch (err) {
          // Vibration API may throw on some browsers/devices
        }
      }
      
      // 🌊 PING RIPPLE: Visual confirmation of tap
      if (!powerSaver) {
        const rippleColor = 
          hitNode.connection_type === "bonded" ? "#B56BFF" :
          hitNode.connection_type === "strong" ? "#FFD36B" :
          "#9CA3AF";
        
        const duration = reduceMotion ? 250 : 450;
        const maxRadius = reduceMotion ? 18 : 28;
        
        ripplesRef.current.push({
          x: hitScreenX,
          y: hitScreenY,
          t0: performance.now(),
          color: rippleColor,
          duration,
          maxRadius
        });
        
        if (ripplesRef.current.length > 8) {
          ripplesRef.current.shift();
        }
      }
      
      // Track star clicked
      track("star_clicked", {
        target_user_id: hitNode.user_id,
        connection_type: hitNode.connection_type,
        strength: hitNode.connection_strength
      });
      
      // Tap-after-zoom debug info
      const hitDebugInfo = {
        type: "hit",
        name: hitNode.display_name || hitNode.user_id,
        dist: bestDist,
        thr: hitWorldRadius,
        scale: view.scale,
      };
      setLocalHitDebug(hitDebugInfo);
      onHitDebug?.(hitDebugInfo);
      
      onNodeSelected?.(hitNode);
    } else {
      console.log('[CanvasClick] No hit (closest was', sortedByDist[0]?.name, 
        'at dist', sortedByDist[0]?.dist, ', threshold:', hitWorldRadius.toFixed(4), ')');
      
      // Tap-after-zoom debug info for misses
      const missDebugInfo = {
        type: "miss",
        name: sortedByDist[0]?.name || "none",
        dist: sortedByDist[0] ? Number(sortedByDist[0].dist) : null,
        thr: hitWorldRadius,
        scale: view.scale,
      };
      setLocalHitDebug(missDebugInfo);
      onHitDebug?.(missDebugInfo);
      
      // FIX: Only emit clear if disableClear is false (card not open)
      if (!disableClear) {
        onNodeSelected?.(null); // Clear selection on empty space click
      } else {
        console.log('[CanvasClick] Skipping clear - disableClear is true');
      }
    }
  };

  // Separate function for tap handling (cleaner separation)
  // NOTE: This is now a backup - onClick is the primary interaction
  const handleCanvasTap = (e) => {
    console.log('[CanvasTap] TAP DETECTED');
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    
    console.log('[CanvasTap] Click at', { clickX, clickY });

    // Use cached screen coordinates for hit testing
    const cachedNodes = screenNodesRef.current || [];
    console.log('[CanvasTap] Testing against', cachedNodes.length, 'nodes');

    let hit = null;

    for (const node of cachedNodes) {
      const dx = clickX - node.screenX;
      const dy = clickY - node.screenY;
      const distSquared = dx * dx + dy * dy;
      const hitRadiusSquared = node.hitRadius * node.hitRadius;

      const isHit = distSquared <= hitRadiusSquared;

      console.log('[HitTest]', isHit ? 'HIT' : 'miss', {
        nodeId: node.id,
        screenX: node.screenX.toFixed(1),
        screenY: node.screenY.toFixed(1),
        hitRadius: node.hitRadius,
        distance: Math.sqrt(distSquared).toFixed(1),
        isHit
      });

      if (isHit) {
        hit = node;
        break; // Stop at first hit
      }
    }

    if (hit) {
      console.log('[CanvasTap] FOUND HIT NODE:', hit.raw.display_name);
      if (onNodeSelected) {
        onNodeSelected(hit.raw);
      }
    } else {
      console.log('[CanvasTap] No node under tap');
      if (onNodeSelected) {
        onNodeSelected(null); // Clear selection
      }
    }
  };

  // Handle pointer cancel (e.g., browser interrupts gesture)
  const handlePointerCancel = (e) => {
    const { pointerId } = e;
    activeTouchesRef.current.delete(pointerId);
    pinchStartRef.current = null;
    isDragging.current = false;
    hasStartedDragging.current = false;
    lastPos.current = null;
    pointerDownPos.current = null;
    pointerDownTime.current = null;
    velocityRef.current = { vx: 0, vy: 0 };
    gestureRef.current.reset(); // Force reset gesture state
    e.currentTarget.style.cursor = 'grab';
  };

  // Safari-proof touch blocking (passive: false is critical)
  // This ensures preventDefault() actually works on mobile browsers
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const blockTouch = (e) => {
      // Allow single touch for pointer events, block multi-touch browser zoom
      if (e.touches && e.touches.length > 1) {
        e.preventDefault();
      }
      // Always block during active gesture to prevent scroll
      if (gestureRef.current.isActive()) {
        e.preventDefault();
      }
    };

    // Register with passive: false to ensure preventDefault works
    canvas.addEventListener("touchstart", blockTouch, { passive: false });
    canvas.addEventListener("touchmove", blockTouch, { passive: false });

    return () => {
      canvas.removeEventListener("touchstart", blockTouch);
      canvas.removeEventListener("touchmove", blockTouch);
    };
  }, []);

  // Zoom handler (scroll wheel)
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY;
    
    // Track first zoom
    markPanZoom("zoom");
    
    // Update ref (no re-render)
    const newScale = clampScale(delta > 0 ? viewRef.current.scale * 0.9 : viewRef.current.scale * 1.1);
    viewRef.current.scale = newScale;
    requestDraw();
    setDisplayScale(newScale); // Update UI display
  };

  // Reset view button handler
  const handleReset = () => {
    // Cancel any running inertia
    if (inertiaAnimationRef.current) {
      cancelAnimationFrame(inertiaAnimationRef.current);
      inertiaAnimationRef.current = null;
    }
    velocityRef.current = { vx: 0, vy: 0 };
    gestureRef.current.reset();
    // Update refs
    viewRef.current = { x: 0, y: 0, scale: 1 };
    requestDraw();
    setDisplayScale(1); // Update UI display
  };

  // Listen for reset view events from header
  useEffect(() => {
    const handleResetEvent = () => handleReset();
    window.addEventListener('constellation:resetView', handleResetEvent);
    return () => window.removeEventListener('constellation:resetView', handleResetEvent);
  }, []);

  return (
    <div 
      ref={containerRef} 
      className="w-full h-full relative bg-black overflow-hidden"
      style={{ touchAction: 'none' }} /* Critical for mobile - prevent browser gesture hijacking */
    >
      <canvas
        ref={canvasRef}
        className="w-full h-full cursor-grab"
        data-testid="universe-canvas"
        style={{ 
          display: 'block', 
          touchAction: 'none',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          zIndex: Z_LAYERS.CANVAS
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onWheel={handleWheel}
        onClick={handleCanvasClick}
      />
      
      {/* Zoom controls overlay - FIX: Position much higher to avoid Emergent watermark */}
      <div 
        className="fixed right-4 flex flex-col gap-2"
        style={{ 
          zIndex: 2147483647, 
          pointerEvents: 'auto',
          bottom: '160px'  // Aggressively lifted above watermark
        }}
      >
        <button
          onClick={() => { 
            markPanZoom("zoom"); 
            const newScale = clampScale(viewRef.current.scale * 1.2);
            viewRef.current.scale = newScale;
            requestDraw();
            setDisplayScale(newScale);
          }}
          className="w-10 h-10 bg-gray-800/90 hover:bg-gray-700/90 rounded-full flex items-center justify-center text-white text-lg transition border border-gray-700"
          aria-label="Zoom in"
        >
          +
        </button>
        <button
          onClick={() => { 
            markPanZoom("zoom"); 
            const newScale = clampScale(viewRef.current.scale * 0.8);
            viewRef.current.scale = newScale;
            requestDraw();
            setDisplayScale(newScale);
          }}
          className="w-10 h-10 bg-gray-800/90 hover:bg-gray-700/90 rounded-full flex items-center justify-center text-white text-lg transition border border-gray-700"
          aria-label="Zoom out"
        >
          −
        </button>
        <button
          onClick={handleReset}
          className="w-10 h-10 bg-gray-800/90 hover:bg-gray-700/90 rounded-full flex items-center justify-center text-white text-xs transition border border-gray-700"
          aria-label="Reset view"
          title="Reset view"
        >
          ⟲
        </button>
      </div>

      {/* Instructions overlay (only show if there are nodes) */}
      {nodes.length > 0 && (
        <div 
          className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-gray-900/90 px-4 py-2 rounded-full text-xs text-gray-300 border border-gray-700"
          style={{ pointerEvents: 'none', zIndex: Z_LAYERS.INSTRUCTIONS }}
        >
          Drag to pan • Pinch to zoom
        </div>
      )}
      
      {/* Zoom level indicator */}
      <div 
        className="absolute bottom-4 left-4 bg-gray-900/90 px-3 py-1.5 rounded-full text-xs text-gray-400 border border-gray-700"
        style={{ pointerEvents: 'none', zIndex: Z_LAYERS.ZOOM_INDICATOR }}
      >
        {Math.round(displayScale * 100)}%
      </div>
      
      {/* Debug panel (only visible when ul_debug_universe=1 in localStorage) */}
      <UniverseDebugPanel 
        gestureMode={debugGestureMode}
        scale={displayScale}
        offset={viewRef.current}
        nodeCount={nodes.length}
        isVisible={isVisible}
        hitDebug={localHitDebug}
      />
    </div>
  );
};

export default ConstellationCanvas;
